import "./dropDown.css";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { updateDropdownValue } from "../../redux/reducers/paginationReducer";
import InfoCircle from "../../assets/info-circle.svg";

interface IDropDownProps {
  options?: any;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  value?: any;
  wrapperClass?: string;
  selectClassName?: string;
  placeHolder?: string;
  name?: string;
  heading?: string;
  inputError?: boolean;
  disabled?: boolean;
  /**
   * Native <select> option popups can overflow small viewports (especially inside modals).
   * Set false to render a constrained in-DOM options panel.
   */
  useNativeSelect?: boolean;
  /** Shown above the custom list when `useNativeSelect` is false; omit for compact lists (e.g. pagination). */
  dropdownListCaption?: React.ReactNode;
}

const CustomDropDown = (props: IDropDownProps) => {
  const {
    options,
    onChange,
    value,
    wrapperClass,
    selectClassName,
    placeHolder,
    name,
    heading,
    inputError,
    disabled,
    useNativeSelect = true,
    dropdownListCaption,
  } = props;
  const dispatch = useDispatch();
  const selectId = useId();

  const dropdownRef: any = useRef(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const addClass = () => {
    dropdownRef.current.children[1].classList.add("setLabel");
  };

  let label =
    placeHolder?.replace(/\b\w/g, (match) => match.toUpperCase()) || "";

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    dispatch(updateDropdownValue(newValue)); // Dispatch action to update Redux state
  };

  const handleCombinedChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    handleChange(event);
    onChange && onChange(event); // Call the external onChange handler if provided
  };

  // Get the full label for the selected option to show in tooltip
  const selectedOption = options?.find(
    (opt: any) => String(opt?.value) === String(value)
  );
  const selectedFullLabel =
    selectedOption?.fullLabel || selectedOption?.label || "";

  const selectedDisplayLabel = useMemo(() => {
    if (value === undefined || value === null || value === "") return "";
    return selectedOption?.label || "";
  }, [selectedOption, value]);

  useEffect(() => {
    if (!isOpen) return;
    const closeIfOutside = (e: Event) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", closeIfOutside);
    document.addEventListener("touchstart", closeIfOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", closeIfOutside);
      document.removeEventListener("touchstart", closeIfOutside);
    };
  }, [isOpen]);

  return (
    <>
      <div className={`${wrapperClass} select-container`} ref={dropdownRef}>
        {useNativeSelect ? (
          <select
            id={selectId}
            onChange={handleCombinedChange}
            value={value || ""}
            placeholder={placeHolder}
            name={name}
            disabled={disabled}
            title={selectedFullLabel} // Show full address on hover for selected option
            className={`${selectClassName} select-dropdown rounded cursor-pointer ${
              inputError && !value
                ? "!border-red-500"
                : "border-[1px] border-[#A4A4A4]"
            } p-[10px] gap-[10px] h-[48px] font-Open text-[12px] text-[#1C1C1C] outline-none`}
            onClick={addClass}
          >
            {heading && (
              <option
                value=""
                className="text-[12px] text-[#777777] absolute leading-4 font-Open"
                disabled
                hidden
              >
                {/* Empty placeholder option - text shown via label element */}
              </option>
            )}
            {options?.map((option: any, index: number) => {
              const fullLabel = option?.fullLabel || option?.label || "";
                return (
                <option
                  key={index}
                  value={option.value}
                  className="text-[12px] text-[#1C1C1C] leading-4 font-Open"
                  disabled={option?.disabled || false}
                  title={fullLabel} // Show full address on hover for each option
                >
                  {option.label}
                </option>
              );
            })}
          </select>
        ) : (
          <button
            type="button"
            id={selectId}
            disabled={disabled}
            title={selectedFullLabel}
            onClick={() => setIsOpen((v) => !v)}
            className={`${selectClassName} select-dropdown rounded cursor-pointer ${
              inputError && !value
                ? "!border-red-500"
                : "border-[1px] border-[#A4A4A4]"
            } p-[10px] gap-[10px] h-[48px] font-Open text-[12px] text-left text-[#1C1C1C] outline-none w-full bg-white`}
          >
            <span className="block pr-6 truncate">
              {selectedDisplayLabel || ""}
            </span>
          </button>
        )}
        <label
          htmlFor={selectId}
          className={` text-[12px] text-[#777777] font-normal font-sans absolute transition-all ease-out ${
            value && "valueFilled"
          } `}
        >
          {heading && !value ? heading : label}
        </label>

        {!useNativeSelect && isOpen && (
          <div
            ref={menuRef}
            className="absolute z-[9999] top-[calc(100%+4px)] left-0 right-0 bg-white border border-[#E8E8E8] rounded-md shadow-lg w-full max-h-[35vh] overflow-auto"
          >
            {dropdownListCaption != null && (
              <div className="px-3 py-2 text-[12px] font-Open text-[#777777] border-b border-[#F0F0F0]">
                {dropdownListCaption}
              </div>
            )}
            {(options || []).map((option: any, index: number) => {
              const isDisabled = option?.disabled || false;
              const isSelected = option?.value === value;
              return (
                <button
                  key={index}
                  type="button"
                  disabled={isDisabled}
                  title={option?.fullLabel || option?.label || ""}
                  className={`w-full text-left px-3 py-2 text-[12px] font-Open ${
                    isSelected ? "bg-[#E5EDFF]" : "bg-white"
                  } ${
                    isDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-[#F6F6F6]"
                  } `}
                  onClick={() => {
                    dispatch(updateDropdownValue(option.value));
                    // Normalize value to string to match <select> event semantics
                    onChange?.({ target: { value: String(option.value) } } as any);
                    setIsOpen(false);
                  }}
                >
                  <span className="block truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* {inputError && !value && (
        <span
          className={`text-[red] transition-all ease-out h-0  h-[18px]  delay-100 font-Open text-[11px] mt-1 px-2 `}
        >
          Field is required
        </span>
      )} */}
      {inputError && !value && (
        <div className="flex items-center gap-x-1 mt-1">
          <img src={InfoCircle} alt="" width={16} height={16} />
          <span className="font-normal text-[#F35838] text-xs leading-3 transition-all ease-out delay-100">
            Field is required
          </span>
        </div>
      )}
    </>
  );
};

export default CustomDropDown;

// import { useRef } from "react";
// import { useDispatch } from "react-redux";
// import { updateDropdownValue } from "../../redux/reducers/paginationReducer";
// import InfoCircle from "../../assets/info-circle.svg";

// interface IDropDownProps {
//   options?: any;
//   onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
//   value?: any;
//   wrapperClass?: string;
//   selectClassName?: string;
//   placeHolder?: string;
//   name?: string;
//   heading?: string;
//   inputError?: boolean;
//   disabled?: boolean;
// }

// const CustomDropDown = (props: IDropDownProps) => {
//   const {
//     options,
//     onChange,
//     value,
//     wrapperClass,
//     selectClassName,
//     placeHolder,
//     name,
//     heading,
//     inputError,
//     disabled,
//   } = props;
//   const dispatch = useDispatch();

//   const dropdownRef: any = useRef(null);

//   const addClass = () => {
//     dropdownRef.current.children[1].classList.add("setLabel");
//   };

//   let label =
//     placeHolder?.replace(/\b\w/g, (match) => match.toUpperCase()) || "";

//   const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
//     const newValue = event.target.value;
//     dispatch(updateDropdownValue(newValue)); // Dispatch action to update Redux state
//   };

//   const handleCombinedChange = (
//     event: React.ChangeEvent<HTMLSelectElement>
//   ) => {
//     handleChange(event);
//     onChange && onChange(event); // Call the external onChange handler if provided
//   };

//   return (
//     <>
//       <div
//         className={`${wrapperClass} relative`}
//         ref={dropdownRef}
//         onClick={addClass}
//       >
//         <select
//           id="selectDropdown"
//           onChange={handleCombinedChange}
//           value={value}
//           placeholder={placeHolder}
//           name={name}
//           disabled={disabled}
//           className={`${selectClassName} rounded border ${
//             inputError && !value ? "border-red-500" : "border-gray-400"
//           } p-2 h-12 text-sm text-gray-700 outline-none w-full appearance-none bg-white bg-no-repeat pr-8 font-sans`}
//           style={{
//             backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="none" width="16" height="16" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>')`,
//             backgroundPosition: "calc(100% - 0.5rem) center",
//           }}
//         >
//           {heading && (
//             <option value={""} className="text-base text-gray-500">
//               {heading}
//             </option>
//           )}
//           {options?.map((option: any, index: number) => (
//             <option
//               key={index}
//               value={option.value}
//               className="text-sm text-gray-700"
//             >
//               {option.label}
//             </option>
//           ))}
//         </select>
//         <label
//           htmlFor="selectDropdown"
//           className={`absolute text-sm text-gray-500 transition-all ${
//             value && "transform -translate-y-5 scale-90"
//           }`}
//         >
//           {label}
//         </label>
//       </div>
//       {inputError && !value && (
//         <div className="flex items-center gap-x-1 mt-1">
//           <img src={InfoCircle} alt="" width={16} height={16} />
//           <span className="font-normal text-red-500 text-xs leading-3 transition-all ease-out delay-100">
//             Field is required
//           </span>
//         </div>
//       )}
//     </>
//   );
// };

// export default CustomDropDown;
